import { createFileRoute } from '@tanstack/react-router';
import TablerIconArrowLeft from '~icons/tabler/arrow-left';

import { Avatar, Link, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page, PageSection } from '#/components/pages';
import fx from '#/fx';
import { Content } from '#/lib/content';
import { canonicalLinkTag, seo } from '#/lib/head';

export const Route = createFileRoute('/_main/customers/$storyId')({
  loader: async ({ params }) => {
    const story = await fx.getCustomerStory({
      data: {
        id: params.storyId,
      },
    });

    return { story };
  },
  head: ({ loaderData, match }) => ({
    meta: seo({
      ...loaderData?.story?.seo,
      title: loaderData?.story?.seo.title ?? 'Customer Story',
      description:
        loaderData?.story?.seo.description ??
        'Read a customer story about how one of our customers use our products and services.',
      keywords: ['customers', 'stories', loaderData?.story?.company.name],
      image: loaderData?.story?.seo.image,
      url: match.pathname,
    }),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: CustomerStoryPage,
});

function CustomerStoryPage() {
  const { story } = Route.useLoaderData();
  return (
    <Page>
      <PageSection>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          className='w-full px-4 md:p-0'
          justifyItems={{ xs: 'center', md: 'start' }}
          spacing={{ xs: 3, md: 2, lg: 10 }}
        >
          <ButtonLink
            to='/customers'
            startIcon={<TablerIconArrowLeft />}
            variant='text'
            className='w-fit'
          >
            Back
          </ButtonLink>
          <div className='max-w-[548px] md:pl-8'>
            <Typography variant='h3' component='h1' className='mb-4'>
              <Content content={story.title} />
            </Typography>
            <Typography
              variant='subtitle1'
              component='div'
              className='mb-12 md:text-2xl text-gray-400 relative'
            >
              <div
                className={
                  'text-gray-200 text-8xl md:text-9xl absolute -top-[12px] -left-[64px] hidden md:block'
                }
              >
                “
              </div>
              <Content content={story.highlight} />
            </Typography>
            <Content content={story.content} styling='prose' className='text-gray-400 text-lg' />
            <Stack direction='row' spacing={3} className='mt-4 sm:mt-12' alignItems='center'>
              <Avatar src={story.avatar.src} alt={story.name} size='large' />
              <Stack>
                <Typography variant='h6' component='h2'>
                  {story.name}
                </Typography>
                <Typography variant='subtitle1' component='div' className='text-gray-400'>
                  {story.role}
                </Typography>
              </Stack>
            </Stack>
          </div>
          <GlowCard border='all' className='h-fit md:max-w-1/3 mt-16 md:mt-0' gutter='xs'>
            <Stack spacing={2}>
              <div>
                <Img
                  location='static'
                  src={story.company.logo.src}
                  alt={story.company.name}
                  height={story.company.logo.height}
                  width={story.company.logo.width}
                  responsive
                  className='w-1/2 min-w-28 max-w-36'
                />
                <Typography variant='h6' component='h2' className='mt-4'>
                  {story.company.name}
                </Typography>
                <Typography variant='body1' component='div' className='text-gray-400' gutterBottom>
                  {story.company.description}
                </Typography>
                <Link
                  href={`https://${story.company.url}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mt-4'
                  color='inherit'
                >
                  {story.company.url}
                </Link>
              </div>
              <div>
                <Typography variant='body1' component='div' className='text-gray-400'>
                  Founded
                </Typography>
                <Typography variant='body1' component='div' className='text-gray-200'>
                  {story.company.headquarters.city}, {story.company.headquarters.state}
                  <span className='mx-2'>•</span>
                  {story.company.founded}
                </Typography>
              </div>
              <div>
                <Typography variant='body1' component='div' className='text-gray-400'>
                  Joggr customer since
                </Typography>
                <Typography variant='body1' component='div' className='text-gray-200'>
                  {story.company.joined}
                </Typography>
              </div>
            </Stack>
          </GlowCard>
        </Stack>
      </PageSection>
    </Page>
  );
}
